"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.healthResponseSchema = exports.apiErrorSchema = exports.saveWorldRequestSchema = exports.worldSchema = exports.worldObjectSchema = exports.createPetRequestSchema = exports.petSchema = exports.objectKindSchema = exports.tileKindSchema = exports.lifeStageSchema = void 0;
const game_core_1 = require("@tama/game-core");
const zod_1 = require("zod");
/**
 * The enums are built from the game-core constants rather than re-typed, so a
 * new tile or piece of furniture cannot silently drift out of the wire format.
 */
exports.lifeStageSchema = zod_1.z.enum(game_core_1.LIFE_STAGES);
exports.tileKindSchema = zod_1.z.enum(game_core_1.TILE_KINDS);
exports.objectKindSchema = zod_1.z.enum(game_core_1.OBJECT_KINDS);
// ---------------------------------------------------------------------------
// Pet
//
// There are no auth schemas: Supabase Auth issues the access token directly to
// the client, and this API only verifies it. See `apps/tama-api/src/auth`.
// ---------------------------------------------------------------------------
exports.petSchema = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string(),
    species: zod_1.z.string(),
    /** Derived from the age; the pet grows but never gets ill and never dies. */
    stage: exports.lifeStageSchema,
    bornAt: zod_1.z.number().int(),
    ageMs: zod_1.z.number().int().nonnegative(),
    /** The server's clock at response time; lets the client correct for drift. */
    serverNow: zod_1.z.number().int(),
});
exports.createPetRequestSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1).max(24),
    species: zod_1.z.string().trim().min(1).max(32).default('blob'),
});
// ---------------------------------------------------------------------------
// World
//
// Dev mode is the only thing that writes this, and it writes whatever the
// person editing drew. The bounds below are what keeps a slip of the brush from
// becoming a megabyte of tiles in the database; the *semantic* repairs — an
// object hanging off the edge, a walled-in spawn — are `normalizeWorld`'s job,
// because rejecting the save would leave the editor with nowhere to put its work.
// ---------------------------------------------------------------------------
const tileCoordinate = zod_1.z.number().int().min(-game_core_1.MAX_WORLD_SIZE).max(game_core_1.MAX_WORLD_SIZE);
exports.worldObjectSchema = zod_1.z.object({
    id: zod_1.z
        .string()
        .trim()
        .min(1)
        .max(40)
        .regex(/^[A-Za-z0-9_-]+$/, 'Object ids are slug-like.'),
    kind: exports.objectKindSchema,
    x: tileCoordinate,
    y: tileCoordinate,
});
const worldSize = zod_1.z.number().int().min(game_core_1.MIN_WORLD_SIZE).max(game_core_1.MAX_WORLD_SIZE);
exports.worldSchema = zod_1.z.object({
    id: zod_1.z
        .string()
        .trim()
        .min(1)
        .max(40)
        .regex(/^[A-Za-z0-9_-]+$/, 'Map ids are slug-like.'),
    name: zod_1.z.string().trim().min(1).max(60),
    width: worldSize,
    height: worldSize,
    /** Row-major, `width * height` long. A mismatch is repaired, not rejected. */
    tiles: zod_1.z.array(exports.tileKindSchema).max(game_core_1.MAX_WORLD_SIZE * game_core_1.MAX_WORLD_SIZE),
    objects: zod_1.z.array(exports.worldObjectSchema).max(game_core_1.MAX_WORLD_OBJECTS),
    spawn: zod_1.z.object({ x: tileCoordinate, y: tileCoordinate }),
});
exports.saveWorldRequestSchema = exports.worldSchema;
// ---------------------------------------------------------------------------
// Errors & health
// ---------------------------------------------------------------------------
exports.apiErrorSchema = zod_1.z.object({
    statusCode: zod_1.z.number().int(),
    /** Machine-readable code, e.g. `PET_NOT_FOUND`, `VALIDATION_FAILED`. */
    code: zod_1.z.string(),
    message: zod_1.z.string(),
    details: zod_1.z.unknown().optional(),
});
exports.healthResponseSchema = zod_1.z.object({
    status: zod_1.z.literal('ok'),
    serverNow: zod_1.z.number().int(),
});
//# sourceMappingURL=schemas.js.map